import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, ListChecks, BookOpen, Gamepad2, Timer, Medal, Crown } from "lucide-react";

const MEDAL = { 1: "#F4A261", 2: "#A8B5C0", 3: "#C9986A" };

export default function LeaderboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/leaderboard").then((r) => setData(r.data)); }, []);

  const weekLabel = data ? new Date(data.week_start + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric" }) : "";

  return (
    <div>
      <TopBar subtitle="Leaderboard" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Back</button>

        <div className="relative grain rounded-3xl overflow-hidden p-8 text-white mb-8" style={{ background: "#E07A5F" }}>
          <div className="flex items-center gap-2 text-white/85"><Trophy size={18} /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Family Leaderboard</span></div>
          <h1 className="font-head text-4xl md:text-5xl font-bold mt-2">This Week's Stars</h1>
          <p className="mt-2 text-white/90">Week of {weekLabel} · Points = chores + lessons + games</p>
        </div>

        {!data ? <p className="text-[#6B7280]">Loading…</p> : data.rows.length === 0 ? (
          <p className="text-[#6B7280]">No students yet.</p>
        ) : (
          <div className="space-y-3">
            {data.rows.map((r, i) => {
              const isMe = data.me && r.student_id === data.me;
              const medal = MEDAL[r.rank];
              return (
                <motion.div key={r.student_id} data-testid={`leaderboard-row-${r.first_name}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={`rounded-2xl border p-5 flex items-center gap-4 ${isMe ? "border-[#E07A5F] bg-[#E07A5F]/5" : "border-[#EAE6DF] bg-white"}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-2xl shrink-0 text-white font-head font-bold text-lg relative"
                    style={{ background: medal || "#3D405B" }}>
                    {r.rank <= 3 ? (r.rank === 1 ? <Crown size={22} /> : <Medal size={20} />) : r.rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-head font-bold text-lg text-[#3D405B] flex items-center gap-2">
                      {r.first_name}{isMe && <span className="text-[10px] font-semibold uppercase tracking-wide text-white bg-[#E07A5F] px-2 py-0.5 rounded-full">You</span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-[#6B7280]">
                      <span className="flex items-center gap-1"><ListChecks size={13} className="text-[#F4A261]" /> {r.chores} chores</span>
                      <span className="flex items-center gap-1"><BookOpen size={13} className="text-[#81B29A]" /> {r.lessons} lessons</span>
                      <span className="flex items-center gap-1"><Gamepad2 size={13} className="text-[#3D405B]" /> {r.games} games</span>
                      <span className="flex items-center gap-1"><Timer size={13} className="text-[#E07A5F]" /> {r.minutes} min earned</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="stat text-3xl font-bold text-[#3D405B]">{r.points}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#6B7280]">points</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        <p className="text-center text-xs text-[#6B7280] mt-8">Everyone's a winner when we work hard for the Lord! Resets every Monday. 🌱</p>
      </main>
    </div>
  );
}
